import React, { useState, useEffect, useRef } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { submitBasicInfo, uploadMultipleDocuments, getDocumentTypes, getProfile, logout } from '../features/auth/authSlice';
import CustomAlert from './CustomAlert';
import AddressInput from './AddressInput';
import { ShimmerDocumentTypes } from './Shimmer';
import './BusinessProfile.css';
import logo from '../assets/xtrafyls.svg';
import businessLogoIcon from '../assets/business_logo.svg';
import { signin_profile, submission_icon, logout_icon } from '../assets';
import { useNavigate } from 'react-router-dom';
import checkedCheckboxIcon from '../assets/images/checked-checkbox-.svg';
import uncheckedCheckboxIcon from '../assets/images/uncheck-checkbox-outline.svg';

// Validation Schema for Step 1
const step1ValidationSchema = Yup.object({
  businessName: Yup.string()
    .required('Business name is required')
    .min(2, 'Business name must be at least 2 characters'),
  contactNumber: Yup.string()
    .required('Contact number is required')
    .matches(/^[0-9]{10}$/, 'Contact number must be exactly 10 digits'),
  email: Yup.string()
    .email('Please enter a valid email address')
    .required('Email is required'),
  address: Yup.string()
    .required('Address is required')
    .min(5, 'Address must be at least 5 characters'),
});

// Validation Schema for Step 2
const step2ValidationSchema = Yup.object({
  documentTypes: Yup.object().test(
    'at-least-one',
    'Please select at least one document type',
    (obj) => Object.values(obj).some(val => val === true)
  ),
  documentName: Yup.string()
    .min(2, 'Document name must be at least 2 characters'),
});

const BusinessProfile = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    isLoading,
    error,
    documentTypes,
    basicInfoCompleted,
    documentsCompleted,
    isVerified: profileVerified
  } = useSelector((state) => state.auth);

  // Set initial step based on localStorage profile status
  const getInitialStep = () => {
    try {
      const profileStatusStr = localStorage.getItem('profileStatus');
      if (profileStatusStr) {
        const profileStatus = JSON.parse(profileStatusStr);
        if (profileStatus.basicInfoCompleted && !profileStatus.documentsCompleted) {
          return 2;
        }
        if (profileStatus.basicInfoCompleted && profileStatus.documentsCompleted) {
          return 2; // Show pending popup
        }
      }
    } catch (e) {
      // Use default
    }
    return 1;
  };

  const [currentStep, setCurrentStep] = useState(getInitialStep());

  // Check if should show pending popup on load
  const shouldShowPendingPopup = () => {
    try {
      const profileStatusStr = localStorage.getItem('profileStatus');
      if (profileStatusStr) {
        const profileStatus = JSON.parse(profileStatusStr);
        return profileStatus.basicInfoCompleted && profileStatus.documentsCompleted && !profileStatus.isVerified;
      }
    } catch (e) {
      // Use default
    }
    return false;
  };

  const [showSuccessPopup, setShowSuccessPopup] = useState(shouldShowPendingPopup());
  const [isVerified, setIsVerified] = useState(false);
  const [initialCheckDone, setInitialCheckDone] = useState(false);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [isJustSubmitted, setIsJustSubmitted] = useState(false); // true = just submitted form, false = pending on refresh
  const hasFetchedRef = useRef(false);

  // Fetch profile and document types on mount - only once
  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;

    const fetchData = async () => {
      try {
        await dispatch(getProfile()).unwrap();
        setProfileLoaded(true);
      } catch (err) {
        setProfileLoaded(true);
      }
      dispatch(getDocumentTypes());
    };
    fetchData();
  }, [dispatch]);

  // Redirect based on profile completion status - run after profile data loads
  useEffect(() => {
    // Wait for profile to actually load
    if (!profileLoaded) {
      return;
    }

    // Fully verified - redirect to dashboard
    if (basicInfoCompleted && documentsCompleted && profileVerified) {
      navigate('/dashboard');
      return;
    }

    // Documents uploaded but not verified - show pending popup
    if (basicInfoCompleted && documentsCompleted && !profileVerified) {
      setCurrentStep(2);
      setIsVerified(false);
      setShowSuccessPopup(true);
      setInitialCheckDone(true);
      return;
    }

    // Basic info done, documents pending - go to step 2
    if (basicInfoCompleted && !documentsCompleted) {
      setCurrentStep(2);
      setInitialCheckDone(true);
      return;
    }

    // Mark check as done for other cases
    setInitialCheckDone(true);
  }, [basicInfoCompleted, documentsCompleted, profileVerified, profileLoaded, navigate]);

  // Custom Alert State
  const [showAlert, setShowAlert] = useState(false);
  const [alertConfig, setAlertConfig] = useState({ type: 'success', title: '', message: '' });

  // Location coordinates state
  const [locationCoords, setLocationCoords] = useState({
    latitude: '',
    longitude: '',
    city: '',
    state: '',
    pincode: ''
  });

  // Custom Alert Helper
  const showCustomAlert = (type, title, message) => {
    setAlertConfig({ type, title, message });
    setShowAlert(true);
  };

  // Get saved business details from localStorage
  const getSavedBusinessDetails = () => {
    try {
      const businessDetailsStr = localStorage.getItem('businessDetails');
      if (businessDetailsStr) {
        return JSON.parse(businessDetailsStr);
      }
    } catch {
      // Use defaults
    }
    return null;
  };

  // Get email from localStorage (from login)
  const getUserEmail = () => {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        return user.email || user.Email || '';
      }
      // Fallback to userEmail if stored separately
      return localStorage.getItem('userEmail') || '';
    } catch {
      return '';
    }
  };

  // Get initial form values with pre-filled data
  const getInitialFormValues = () => {
    const savedDetails = getSavedBusinessDetails();
    return {
      // Step 1 - Pre-fill from saved business details
      businessName: savedDetails?.BusinessName || savedDetails?.business_name || '',
      contactNumber: savedDetails?.BusinessPhone || savedDetails?.business_phone || '',
      email: savedDetails?.Email || savedDetails?.email || getUserEmail(),
      address: savedDetails?.BusinessAddress || savedDetails?.business_address || '',
      businessLogo: null,

      // Step 2 - documentTypes will be dynamically populated based on API response
      documentTypes: {},
      documentName: ''
    };
  };

  // Initial form values
  const initialValues = getInitialFormValues();

  // Disable body scroll when popup is open
  useEffect(() => {
    if (showSuccessPopup) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [showSuccessPopup]);

  // Close popup on ESC key
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && showSuccessPopup) {
        setShowSuccessPopup(false);
      }
    };
    
    if (showSuccessPopup) {
      document.addEventListener('keydown', handleEsc);
      return () => document.removeEventListener('keydown', handleEsc);
    }
  }, [showSuccessPopup]);

  // 🆕 Handle Step 1 Submit - BasicInfo API
  const handleStep1Submit = async (values) => {
    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('BusinessName', values.businessName);
      formData.append('BusinessAddress', values.address);
      formData.append('BusinessPhone', values.contactNumber);

      // Add location data if available
      if (locationCoords.latitude && locationCoords.longitude) {
        formData.append('Latitude', locationCoords.latitude.toString());
        formData.append('Longitude', locationCoords.longitude.toString());
        if (locationCoords.city) formData.append('City', locationCoords.city);
        if (locationCoords.state) formData.append('State', locationCoords.state);
        if (locationCoords.pincode) formData.append('Pincode', locationCoords.pincode);
      }

      if (values.businessLogo) {
        formData.append('BusinessLogo', values.businessLogo);
      }

      await dispatch(submitBasicInfo(formData)).unwrap();
      setCurrentStep(2);
    } catch (error) {
      showCustomAlert('error', 'Save Failed', error || 'Failed to save basic info');
    }
  };

  // Handle location change from AddressInput
  const handleLocationChange = (locationData) => {
    setLocationCoords({
      latitude: locationData.latitude,
      longitude: locationData.longitude,
      city: locationData.city || '',
      state: locationData.state || '',
      pincode: locationData.pincode || ''
    });
  };

  // Handle Next (Step 1 to Step 2)
  const handleNext = async (validateForm, setTouched, values) => {
    const errors = await validateForm();
    
    if (!errors.businessName && !errors.contactNumber && !errors.email && !errors.address) {
      // Call API to save Step 1
      await handleStep1Submit(values);
    } else {
      // Touch all step 1 fields to show errors
      setTouched({
        businessName: true,
        contactNumber: true,
        email: true,
        address: true
      });
    }
  };

  // Handle Back (Step 2 to Step 1)
  const handleBack = () => {
    setCurrentStep(1);
  };

  // Handle Final Submit - Upload Documents (Multiple at once)
  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      // Get selected document types - now using dynamic doc_${id} format
      const selectedDocIds = Object.entries(values.documentTypes)
        .filter(([key, value]) => value === true && key.startsWith('doc_'))
        .map(([key]) => parseInt(key.replace('doc_', '')));

      // Build FormData for multiple documents
      const formData = new FormData();
      let docIndex = 0;

      for (const docId of selectedDocIds) {
        const fileKey = `doc_${docId}_file`;
        const file = values[fileKey];

        if (file) {
          // Append in format: documents[0][DocumentTypeId], documents[0][Document]
          formData.append(`documents[${docIndex}][DocumentTypeId]`, docId);
          formData.append(`documents[${docIndex}][Document]`, file);

          docIndex++;
        }
      }

      // Check if any documents were added
      if (docIndex === 0) {
        showCustomAlert('error', 'No Document', 'Please upload at least one document file');
        setSubmitting(false);
        return;
      }

      // Add CustomName if "Other Document" is selected (code === 'OTHER')
      const otherDocType = documentTypes.find(d => d.code === 'OTHER');
      if (otherDocType && values.documentTypes[`doc_${otherDocType.id}`] && values.documentName) {
        formData.append('CustomName', values.documentName);
      }

      // Single API call to upload all documents
      const response = await dispatch(uploadMultipleDocuments(formData)).unwrap();

      // Check verification status from response
      setIsVerified(response.IsVerified || false);

      setSubmitting(false);
      setIsJustSubmitted(true); // Mark as just submitted
      setShowSuccessPopup(true);

    } catch (error) {
      setSubmitting(false);
      showCustomAlert('error', 'Upload Failed', error || 'Failed to upload documents');
    }
  };

  // Handle Go to Dashboard
  const handleGoToDashboard = () => {
    setShowSuccessPopup(false);
    navigate('/dashboard');
  };

  // Get current validation schema based on step
  const getValidationSchema = () => {
    return currentStep === 1 ? step1ValidationSchema : step2ValidationSchema;
  };

  return (
    <div className="business-profile-container">
      {/* Left Side - Purple Section */}
      <div className="business-profile-left">
        <div className="left-content">
          <img src={logo} alt="Xtrafyl" className="logo" />
          
          <div className="hero-section">
            <h1 className="hero-title">
              Set Up Your Business<br />
              in Minutes
            </h1>
            <p className="hero-description text-big-body">
              Complete these quick steps to verify your account, add services, and go live with Xtrafyl.
            </p>
          </div>
        </div>

      </div>

      {/* Right Side - Form */}
      <div className="business-profile-right">
        <div className="form-content">
          {/* Progress Steps Header with Logout */}
          <div className="progress-header">
            <div className="progress-steps">
              {[1, 2].map((step) => (
                <div key={step} className="step-item">
                  <div className={`step-circle ${currentStep >= step ? 'active' : ''} ${currentStep > step ? 'completed' : ''}`}>
                    {step}
                  </div>
                  {step < 2 && (
                    <div className={`step-line ${currentStep > step ? 'completed' : ''}`}></div>
                  )}
                </div>
              ))}
            </div>
            <button
              type="button"
              className="logout-icon-btn"
              onClick={() => {
                dispatch(logout());
                navigate('/signin');
              }}
              title="Logout"
            >
              <img src={logout_icon} alt="Logout" style={{ width: '24px', height: '24px' }} />
            </button>
          </div>

          <Formik
            initialValues={initialValues}
            validationSchema={getValidationSchema()}
            onSubmit={handleSubmit}
            validateOnChange={true}
            validateOnBlur={true}
          >
            {({ errors, touched, isSubmitting, setFieldValue, values, validateForm, setTouched, isValid }) => (
              <Form>
                {/* Step 1: Business Profile */}
                {currentStep === 1 && (
                  <div className="form-step">
                    <div className="step-header">
                      <h2>Step 1: Business Profile</h2>
                      <p className="text-small-body">
                        Join our network of providers delivering fuel, charging, and auto care on-demand.
                      </p>
                    </div>

                    <div className="form-container business_profile_form">
                      <div className="form-group business_profile_group">
                        <label className="text-label">Business Name</label>
                        <div className="input-wrapper">
                          <svg className="input-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path d="M3.33333 16.6667H16.6667M10 2.5V16.6667M10 2.5L5 7.5M10 2.5L15 7.5" stroke="#64748B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          <Field
                            type="text"
                            name="businessName"
                            placeholder="Business Name"
                            className={`${
                              errors.businessName && touched.businessName ? 'input-error' : ''
                            } ${!errors.businessName && touched.businessName ? 'input-success' : ''}`}
                          />
                          {!errors.businessName && touched.businessName && (
                            <svg className="input-success-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
                              <path d="M16.6667 5L7.50004 14.1667L3.33337 10" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          )}
                        </div>
                        <ErrorMessage name="businessName" component="div" className="error-message" />
                      </div>

                      <div className="form-group business_profile_group">
                        <label className="text-label">Contact Number</label>
                        <div className="input-wrapper">
                          <svg className="input-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path d="M18.3333 14.1V16.6C18.3343 16.8321 18.2867 17.0618 18.1937 17.2745C18.1008 17.4871 17.9644 17.678 17.7934 17.8349C17.6224 17.9918 17.4205 18.1112 17.2006 18.1856C16.9808 18.26 16.7477 18.2876 16.5167 18.2667C13.9523 17.988 11.489 17.1118 9.32499 15.7083C7.31151 14.4289 5.60443 12.7218 4.32499 10.7083C2.91663 8.53434 2.04019 6.05908 1.76666 3.48334C1.74583 3.25294 1.77321 3.02058 1.84707 2.80127C1.92092 2.58196 2.03963 2.38049 2.19562 2.2096C2.35162 2.03871 2.54149 1.90218 2.75314 1.80869C2.9648 1.7152 3.1936 1.66698 3.42499 1.66668H5.92499C6.32953 1.6627 6.72148 1.80582 7.02812 2.06953C7.33476 2.33324 7.53505 2.69946 7.59166 3.10001C7.69718 3.90006 7.89286 4.68562 8.17499 5.44168C8.28713 5.73996 8.31137 6.06414 8.24491 6.37576C8.17844 6.68738 8.02404 6.97343 7.79999 7.20001L6.74166 8.25834C7.92795 10.3446 9.65536 12.072 11.7417 13.2583L12.8 12.2C13.0266 11.976 13.3126 11.8216 13.6243 11.7551C13.9359 11.6886 14.26 11.7129 14.5583 11.825C15.3144 12.1071 16.0999 12.3028 16.9 12.4083C17.3048 12.4655 17.6745 12.6694 17.9388 12.9813C18.2032 13.2931 18.3435 13.6912 18.3333 14.1Z" stroke="#64748B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          <Field
                            type="tel"
                            name="contactNumber"
                            placeholder="Phone number"
                            maxLength={10}
                            onKeyPress={(e) => {
                              // Only allow digits
                              if (!/[0-9]/.test(e.key)) {
                                e.preventDefault();
                              }
                            }}
                            onChange={(e) => {
                              // Only allow digits and max 10
                              const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                              setFieldValue('contactNumber', value);
                            }}
                            className={`${
                              errors.contactNumber && touched.contactNumber ? 'input-error' : ''
                            } ${!errors.contactNumber && touched.contactNumber ? 'input-success' : ''}`}
                          />
                          {!errors.contactNumber && touched.contactNumber && (
                            <svg className="input-success-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
                              <path d="M16.6667 5L7.50004 14.1667L3.33337 10" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          )}
                        </div>
                        <ErrorMessage name="contactNumber" component="div" className="error-message" />
                      </div>

                      <div className="form-group business_profile_group">
                        <label className="text-label">Email-Id</label>
                        <div className="input-wrapper">
                          <svg className="input-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path d="M3.33333 3.33334H16.6667C17.5833 3.33334 18.3333 4.08334 18.3333 5.00001V15C18.3333 15.9167 17.5833 16.6667 16.6667 16.6667H3.33333C2.41667 16.6667 1.66667 15.9167 1.66667 15V5.00001C1.66667 4.08334 2.41667 3.33334 3.33333 3.33334Z" stroke="#64748B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M18.3333 5L10 10.8333L1.66667 5" stroke="#64748B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          <Field
                            type="email"
                            name="email"
                            placeholder="Email"
                            className={`${
                              errors.email && touched.email ? 'input-error' : ''
                            } ${!errors.email && touched.email ? 'input-success' : ''}`}
                          />
                          {!errors.email && touched.email && (
                            <svg className="input-success-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
                              <path d="M16.6667 5L7.50004 14.1667L3.33337 10" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          )}
                        </div>
                        <ErrorMessage name="email" component="div" className="error-message" />
                      </div>

                      <div className="form-group business_profile_group">
                        <label className="text-label">Address</label>
                        <AddressInput
                        className="address_input"
                          value={values.address}
                          onChange={(e) => setFieldValue('address', e.target.value)}
                          onBlur={() => setTouched({ ...touched, address: true })}
                          placeholder="Enter your address"
                          name="address"
                          error={errors.address}
                          touched={touched.address}
                          onLocationChange={handleLocationChange}
                        />
                        <ErrorMessage name="address" component="div" className="error-message" />
                      </div>

                      <div className="form-group business_profile_group">
                        <label className="text-label">Business Logo</label>
                        <div className="file-upload-wrapper">
                          <div className="file-upload-input">
                            <img src={businessLogoIcon} alt="" style={{ width: '24px', height: '24px' }} />
                            <span>Business Logo</span>
                          </div>
                          <label className="file-upload-button">
                            Upload
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) {
                                  setFieldValue('businessLogo', file);
                                }
                              }}
                              hidden
                            />
                          </label>
                        </div>
                        {values.businessLogo && (
                          <div className="uploaded-file-info">
                            <span className="uploaded-file-name">{values.businessLogo.name}</span>
                            <button
                              type="button"
                              className="remove-file-btn"
                              onClick={() => setFieldValue('businessLogo', null)}
                            >
                              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path d="M12 4L4 12M4 4L12 12" stroke="#EF4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </button>
                          </div>
                        )}
                      </div>

                      <button 
                        type="button" 
                        onClick={() => handleNext(validateForm, setTouched, values)} 
                        className="continue-button text-button"
                        disabled={isLoading}
                      >
                        {isLoading ? 'SAVING...' : 'SAVE & CONTINUE'}
                      </button>
                    </div>
                  </div>
                )}

                {/* Step 2: License Information */}
                {currentStep === 2 && (
                  <div className="form-step">
                    <div className="step-header">
                      <h2>Step 2: License Information</h2>
                      <p className="text-small-body">
                        Add your business documents for verification and compliance
                      </p>
                    </div>

                    <div className="form-container business_profile_form">
                      <div className="form-group business_profile_group">
                        <label className="text-label">Document Type</label>
                        {!documentTypes || documentTypes.length === 0 ? (
                          <ShimmerDocumentTypes count={6} />
                        ) : (
                        <div className="bp-document-types-container">
                          {documentTypes
                              .filter(doc => doc.is_active)
                              .map(doc => (
                                <div key={doc.id} className="bp-document-checkbox-item">
                                  <div className="bp-checkbox-row">
                                    <label className="custom-checkbox">
                                      <Field
                                        type="checkbox"
                                        name={`documentTypes.doc_${doc.id}`}
                                        onChange={(e) => {
                                          const isChecked = e.target.checked;
                                          setFieldValue(`documentTypes.doc_${doc.id}`, isChecked);
                                          // Clear file when unchecked
                                          if (!isChecked) {
                                            setFieldValue(`doc_${doc.id}_file`, null);
                                          }
                                        }}
                                      />
                                      <img src={checkedCheckboxIcon} alt="" className="checkbox-icon checked" />
                                      <img src={uncheckedCheckboxIcon} alt="" className="checkbox-icon unchecked" />
                                      <span>{doc.name}</span>
                                    </label>

                                    {/* Show Choose File button for non-OTHER documents when checked */}
                                    {values.documentTypes[`doc_${doc.id}`] && doc.code !== 'OTHER' && (
                                      <label className="file-upload-button bp-choose-btn">
                                        Upload
                                        <input
                                          type="file"
                                          accept=".pdf,.jpg,.jpeg,.png"
                                          onChange={(e) => {
                                            const file = e.target.files[0];
                                            if (file) {
                                              setFieldValue(`doc_${doc.id}_file`, file);
                                            }
                                          }}
                                          hidden
                                        />
                                      </label>
                                    )}
                                  </div>
                                  {/* Show uploaded file name below */}
                                  {values[`doc_${doc.id}_file`] && doc.code !== 'OTHER' && (
                                    <div className="uploaded-file-info bp-uploaded-file">
                                      <span className="uploaded-file-name">{values[`doc_${doc.id}_file`].name}</span>
                                      <button
                                        type="button"
                                        className="remove-file-btn"
                                        onClick={() => setFieldValue(`doc_${doc.id}_file`, null)}
                                      >
                                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                          <path d="M12 4L4 12M4 4L12 12" stroke="#EF4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                      </button>
                                    </div>
                                  )}

                                  {/* Show Upload Selected Documents section for OTHER */}
                                  {doc.code === 'OTHER' && values.documentTypes[`doc_${doc.id}`] && (
                                    <div className="bp-other-section">
                                      <label className="text-label bp-upload-label">Upload Selected Documents</label>
                                      <div className="file-upload-wrapper">
                                        <div className="file-upload-input">
                                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                            <path d="M19 3H5C3.89543 3 3 3.89543 3 5V19C3 20.1046 3.89543 21 5 21H19C20.1046 21 21 20.1046 21 19V5C21 3.89543 20.1046 3 19 3Z" stroke="#64748B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                            <path d="M8.5 10C9.32843 10 10 9.32843 10 8.5C10 7.67157 9.32843 7 8.5 7C7.67157 7 7 7.67157 7 8.5C7 9.32843 7.67157 10 8.5 10Z" stroke="#64748B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                            <path d="M21 15L16 10L5 21" stroke="#64748B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                          </svg>
                                          <span>Upload Documents</span>
                                        </div>
                                        <label className="file-upload-button">
                                          Upload
                                          <input
                                            type="file"
                                            accept=".pdf,.jpg,.jpeg,.png"
                                            onChange={(e) => {
                                              const file = e.target.files[0];
                                              if (file) {
                                                setFieldValue(`doc_${doc.id}_file`, file);
                                              }
                                            }}
                                            hidden
                                          />
                                        </label>
                                      </div>
                                      {/* Show uploaded file */}
                                      {values[`doc_${doc.id}_file`] && (
                                        <div className="uploaded-file-info">
                                          <span className="uploaded-file-name">{values[`doc_${doc.id}_file`].name}</span>
                                          <button
                                            type="button"
                                            className="remove-file-btn"
                                            onClick={() => setFieldValue(`doc_${doc.id}_file`, null)}
                                          >
                                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                              <path d="M12 4L4 12M4 4L12 12" stroke="#EF4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              ))}
                        </div>
                        )}
                        {errors.documentTypes && touched.documentTypes && (
                          <div className="error-message">{errors.documentTypes}</div>
                        )}
                      </div>

                      <button
                        type="submit"
                        className="continue-button text-button"
                        disabled={isSubmitting || isLoading}
                      >
                        {isSubmitting || isLoading ? 'UPLOADING...' : 'SAVE & SUBMIT'}
                      </button>
                    </div>
                  </div>
                )}
              </Form>
            )}
          </Formik>
        </div>
      </div>

      {/* Popup - Shows different content based on IsVerified and isJustSubmitted */}
      {showSuccessPopup && (
        <div className="popup-overlay" onClick={isVerified ? () => setShowSuccessPopup(false) : undefined}>
          <div className="popup-content" onClick={(e) => e.stopPropagation()}>
            {/* Close Button - Always show, redirects to signin for pending */}
            <button
              className="popup-close-btn"
              onClick={() => {
                setShowSuccessPopup(false);
                if (!isVerified) {
                  dispatch(logout());
                  navigate('/signin');
                }
              }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18M6 6L18 18" stroke="#64748B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>

            {/* Icon */}
            <div className="popup-icon">
              {isJustSubmitted ? (
                <img src={submission_icon} alt="Submission" style={{ width: '120px', height: '120px' }} />
              ) : (
                <svg width="120" height="120" viewBox="0 0 120 120" fill="none">
                  <circle cx="60" cy="60" r="60" fill="#F59E0B" fillOpacity="0.1"/>
                  <circle cx="60" cy="60" r="48" fill="#F59E0B"/>
                  <path d="M60 40V65M60 75H60.02" stroke="white" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </div>

            {/* Content - Different based on verification status and submission state */}
            {isVerified ? (
              <>
                <h3 className="popup-title">Verification Successful!</h3>
                <p className="popup-message">
                  Your documents have been verified successfully. You can now start using all features of Xtrafyl.
                </p>
                {/* Go to Dashboard Button - Only when verified */}
                <button
                  className="dashboard-button"
                  onClick={handleGoToDashboard}
                >
                  GO TO DASHBOARD
                </button>
              </>
            ) : isJustSubmitted ? (
              <>
                <h3 className="popup-title">Thank you for submission</h3>
                <p className="popup-message">
                  We have received your request and will review your account status as soon as possible.
                  An email confirmation will be sent once your documents are verified.
                </p>
              </>
            ) : (
              <>
                <h3 className="popup-title">Verification Pending</h3>
                <p className="popup-message">
                  Your documents are under review. An email confirmation will be sent once your documents are verified.
                </p>
              </>
            )}
          </div>
        </div>
      )}

      {/* Custom Alert */}
      <CustomAlert
        show={showAlert}
        onClose={() => setShowAlert(false)}
        type={alertConfig.type}
        title={alertConfig.title}
        message={alertConfig.message}
      />
    </div>
  );
};

export default BusinessProfile;